import DrillDownLink from "@components/drillDown";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { TGetValueFn } from "@components/smart/FieldInfo";
import { ICellValueObject } from "@components/table";
import { ICompanyPermissionEntity, ICompanyRoleEntity, IUserEntity } from "@odata/GeneratedEntityTypes";
import { CompanyRoleTypeCode, UserStatusCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { UserNameStyled } from "@pages/admin/users/Users.styles";
import customFetch from "@utils/customFetch";
import { logger } from "@utils/log";
import { initials } from "@utils/string";
import { cloneDeep } from "lodash";
import React from "react";
import { DefaultTheme } from "styled-components";

import UserInitial from "../../../components/token/UserInitial";
import { REST_API_URL } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { TRecordString, TValue } from "../../../global.types";
import BindingContext, { IEntity, TEntityKey } from "../../../odata/BindingContext";
import { ROUTE_USERS } from "../../../routes";
import TestIds from "../../../testIds";
import { getUtcDate } from "../../../types/Date";
import memoizeOneWithParamsCheck from "../../../utils/memoizeOne";
import { FormStorage, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";

export const CompaniesPath = "##companies##";
export const CompanyRolesPermissionsOverviewPath = BindingContext.localContext("CompanyRolesPermissionsOverview");
export const RoleIdPath = "##roleId##";
export const CustomerRolesPath = BindingContext.localContext("CustomerRoles");
export const GeneralRolePermissionsOverviewPath = BindingContext.localContext("GeneralRolePermissionsOverview");

export const COMPANY_ROLE_LINES_PATH = "##companyRoleLines##";

export const OwnerGeneralRoleId = -1;
export const CustomerCompanyRoleId = -2;
export const CustomerGeneralRoleId = -3;
export const EmployeeCompanyRoleId = -3;
export const AccountantGeneralRoleId = -4;
export const invitationInfoPath = BindingContext.localContext("InvitationInfo");
export const ownerRoleConfirmInfoPath = BindingContext.localContext("OwnerRoleConfirmInfo");
export const activePath = BindingContext.localContext("Active");

interface ICompanyRoleLineItem {
    [RoleIdPath]: number;
    [CompaniesPath]: number[];
}

export interface IUserEntityExtended extends IUserEntity {
    [COMPANY_ROLE_LINES_PATH]: ICompanyRoleLineItem[]
}

export interface IPermissionsWithCompanies extends ICompanyPermissionEntity {
    companies: number[];
}

export interface IPermissionItemProps {
    iconName: string;
    agendas: string;
    permission: IPermissionsWithCompanies;
    allCompanies: TRecordString;
}

export interface IUsersFormCustomData extends IFormStorageDefaultCustomData {
    companyRolesSelectItems?: ISelectItem<number>[];
    permissions: IPermissionsWithCompanies[];
}

export type TUsersFormStorage = FormStorage<IUserEntityExtended, IUsersFormCustomData>;

export function isEntityOwner(context: IAppContext, entity: IEntity): boolean {
    return !entity?.CreatedBy?.Id || entity?.CreatedBy?.Id === context.getData()?.userSettings?.Id;
}

/** According to spec, user initials should always be just two letters, even if user has multiple names. */
export const getUserNameInitials = (user: IUserEntity): string => {
    if (!user) {
        return "";
    }

    return initials(`${user.FirstName ?? ""} ${user.LastName ?? ""}`);
};

export function isAgendaCustomer(context: IAppContext, user: IUserEntity): boolean {
    const companyId = context.getCompanyId();
    if (!companyId) {
        return false;
    }
    const currentRole = user?.CompanyRoles?.find(role => role.Company?.Id === companyId);
    return currentRole?.CompanyRole?.TypeCode === CompanyRoleTypeCode.CustomerRole;
}

export const currentUserIsCustomer = memoizeOneWithParamsCheck((context: IAppContext): boolean => {
    return isAgendaCustomer(context, context.getData().userSettings);
});

export interface IEmailInfoDetail {
    Email: string;
    DateEmailSent: Date;
}

export const getUserStatusColor = (status: UserStatusCode): keyof DefaultTheme => {
    switch (status) {
        case UserStatusCode.Active:
            return "C_SEM_text_good";
        case UserStatusCode.Invited:
        case UserStatusCode.WaitingForAcceptanceOfNewRole:
            return "C_SEM_text_warning";
        default:
            return "C_TEXT_primary";
    }
};

export interface INamedPerson {
    FirstName?: string;
    LastName?: string;
}

export const universalPersonNameFormatter = (person: INamedPerson): string => {
    const { FirstName, LastName } = person ?? {};
    const name = [FirstName ?? "", LastName ?? ""].join(" ").trim();

    return name;
};

const userInitialStyles: React.CSSProperties = { marginRight: "8px", position: "relative" };

export function transformUserSelectItems(items: ISelectItem[]): ISelectItem[] {
    return items.map(item => {
        const person = item.additionalData as INamedPerson;
        const name = universalPersonNameFormatter(person);
        const shortCut = getUserNameInitials(person);

        return {
            ...item,
            itemContent: (<UserInitial title={shortCut} tooltip={name} style={userInitialStyles} isBig={false}/>)
        };
    });
}

export const isUserOwner = (user: IUserEntity): boolean => {
    return user?.GeneralRole?.Id === OwnerGeneralRoleId;
};

export const isUserCustomer = (user: IUserEntity): boolean => {
    return user?.GeneralRole?.Id === CustomerGeneralRoleId;
};

export const isUserEmployee = (context: IAppContext): boolean => {
    const user = context.getData().userSettings;
    const companyId = context.getCompanyId();

    return !!user?.CompanyRoles?.filter(role => role.Company?.Id === companyId)
        .some(role => role.CompanyRole.Id === EmployeeCompanyRoleId);
};

export const isCustomerGeneralRole: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const generalRole = storage.data.entity?.GeneralRole.Id;
    return generalRole === CustomerGeneralRoleId;
};

export function getCompanyRoleSelectItems(storage: TUsersFormStorage, selectedRoleId?: number): ISelectItem[] {
    const { companyRolesSelectItems } = storage.getCustomData();
    const isCustomer = isCustomerGeneralRole({ storage });

    const items = (cloneDeep(companyRolesSelectItems) ?? [])
            .filter(item => (item.additionalData as ICompanyRoleEntity).TypeCode === (isCustomer ? CompanyRoleTypeCode.CustomerRole : CompanyRoleTypeCode.UserRole));

    const lines = storage.getValueByPath(COMPANY_ROLE_LINES_PATH);
    if (lines?.length) {
        // disable used items, so they can't be selected again
        for (const line of storage.getValueByPath(COMPANY_ROLE_LINES_PATH)) {
            if (line[RoleIdPath] === selectedRoleId) {
                continue;
            }
            const item = items.find(item => item.id === line[RoleIdPath]);
            if (item) {
                item.isDisabled = true;
            }
        }
    }
    return items;
}

export interface IUserNameWithAvatarFormatterOptions {
    isBold?: boolean;
    withLink?: boolean;
    isBig?: boolean;
    suffix?: string;
    isInTable?: boolean;
}

export const userNameWithAvatarTableFormatter = (val: TValue, args?: IFormatOptions): (ICellValueObject | string) => {
    return userNameWithAvatarFormatter(val, args, { isInTable: true });
};


export const userNameWithAvatarFormatter = (val: TValue, args?: IFormatOptions,
                                            {
                                                isBold = true,
                                                withLink = false,
                                                isBig,
                                                suffix = "",
                                                isInTable = false
                                            }: IUserNameWithAvatarFormatterOptions = {
                                                isBold: true,
                                                withLink: false,
                                                suffix: "",
                                                isInTable: false
                                            }): (ICellValueObject | string) => {
    if (!val) {
        return "";
    }

    const user = args.entity as IUserEntity;
    const name = universalPersonNameFormatter(user);
    const shortCut = getUserNameInitials(user);
    const style: React.CSSProperties = {};

    if (isInTable && user.StatusCode === UserStatusCode.Inactive) {
        style.opacity = 0.5;
    }

    const content = (<>
        <UserInitial title={shortCut} tooltip={name} isBig={isBig} style={style}/>
        <UserNameStyled isBold={isBold} isBig={isBig} style={style} data-testid={TestIds.Text}>{name}{suffix}</UserNameStyled>
    </>);

    const value = withLink
        ? <DrillDownLink to={`${ROUTE_USERS}/${args.entity.Id}`}>
            {content}
        </DrillDownLink>
        : content;

    return {
        value,
        tooltip: `${name}${suffix}`
    };
};


const GET_INVITATION_INFO_URL = `${REST_API_URL}/invitation/Detailforuser`;

export async function getInvitationInfo(userId: TEntityKey): Promise<IEmailInfoDetail> {
    if (!userId) {
        return null;
    }

    try {
        const res = await customFetch(`${GET_INVITATION_INFO_URL}/${userId}`);

        if (res.ok && res.status !== 204) {
            const emailInfo = await res.json();
            emailInfo.DateEmailSent = getUtcDate(emailInfo.DateEmailSent);

            return emailInfo as IEmailInfoDetail;
        }
    } catch (error) {
        logger.error("getInvitationInfo failed", error);
    }

    return null;
}